import React from 'react';
import { Button } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { Image } from 'common/components';
import { Label } from 'common/components/Label';
import { countFormatter, priceFormatter } from 'common/helpers/formatter.helper';
import { DEFAULT_VALUE_0, VISIBLE_IMAGES_LIMIT_3, VISIBLE_IMAGES_LIMIT_4 } from 'common/config';
import { IAccountShort, ISupplyShort } from 'common/models';
import { Affiliate, ArrowRightLong, Delivery, User } from 'common/svg';
import { getPriceToDisplay } from 'common/helpers';
import { ESupplyGroupType, ESupplyStatus } from 'entities/Supply/Supply.enum';
import {
  IGroupedByDeliveryDateSupply,
  IGroupedSupplyGroup,
  IGroupedSupplyGroupData,
  ISupply,
  ISupplyGroupItem,
} from 'entities/Supply/Supply.models';
import { getUserName } from 'entities/User/User.helper';
import { SupplyStatus } from 'entities/Supply/components/SupplyStatus';
import { EUserRole } from 'entities/User/User.enum';
import { getRequestIdForView } from 'entities/Request/Request.helper';
import { getIsSelfRegistered } from 'entities/Account/Account.helper';

dayjs.extend(isSameOrAfter);

export const getSupplyStatus = (status?: string) => {
  return {
    isCreatedSupply: status === ESupplyStatus.Created,
    isPendingSupply: status === ESupplyStatus.Pending,
    isApprovedSupply: status === ESupplyStatus.Approved,
    isProcessingSupply: status === ESupplyStatus.Processing,
    isDeliverySupply: status === ESupplyStatus.Delivery,
    isDeliveredSupply: status === ESupplyStatus.Delivered,
    isCancelledSupply: status === ESupplyStatus.Cancelled,
  };
};

export const getSupplyFormattedDate = (format: string, date: string) => {
  if (dayjs().isSame(date, 'day')) {
    return 'Сегодня';
  }

  return dayjs(date).locale('ru').format(format);
};

export const getGroupedSuppliesCount = (groupedSupplyList: IGroupedByDeliveryDateSupply[]) => {
  return groupedSupplyList.reduce((acc, group) => acc + group.supplies.length, DEFAULT_VALUE_0);
};

export const getSuppliesCount = (supplies: ISupplyShort[]) => {
  return supplies.reduce((acc, supply) => acc + supply.count, DEFAULT_VALUE_0);
};

export const filterSuppliesByStatuses = (supplies: ISupplyShort[], statuses: string[]) => {
  return supplies.filter((supply) => statuses.includes(supply.status));
};

export const excludeSuppliesByStatuses = (supplies: ISupplyShort[], statuses: string[]) => {
  return supplies.filter((supply) => !statuses.includes(supply.status));
};

export const mapSupplyListToDataSource = (supplyList: ISupply[]) => {
  const map = new Map<string, ISupply[]>();

  supplyList.forEach((supply) => {
    let key: string = '';

    if (supply.deliveryDate) {
      key = supply.deliveryDate;
    }

    if (!map.has(key)) {
      map.set(key, []);
    }

    map.get(key)?.push(supply);
  });

  const result: ISupplyGroupItem[] = [];

  const dateGroups = Array.from(map.entries())
    .filter(([k]) => /^\d{4}-\d{2}-\d{2}/.test(k))
    .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime());

  dateGroups.forEach(([key, items]) => {
    result.push({
      key,
      type: ESupplyGroupType.Date,
      label: getSupplyFormattedDate('DD.MM.YYYY, dddd', key),
    });
    items.forEach((item) => {
      result.push({
        key: item.id,
        type: ESupplyGroupType.Data,
        data: item,
      });
    });
  });

  return result;
};

export const mapGroupedSupplyListToDataSource = (groupedSupplyList: IGroupedByDeliveryDateSupply[]) => {
  const result: IGroupedSupplyGroup[] = [];

  groupedSupplyList
    .filter((group) => !!group.deliveryDate)
    .forEach((group) => {
      const groupedSupplies = group.supplies.reduce((acc: { [key: string]: IGroupedSupplyGroupData }, supply) => {
        const { id, subdivision, user, seller, address } = supply;
        const groupKey = `${address?.name}|${getUserName.fullName(user.lastName, user.firstName, user.patronymic)}`;

        if (!acc[groupKey]) {
          acc[groupKey] = {
            key: id,
            subdivision,
            user,
            seller,
            address,
            supplies: [],
          };
        }

        acc[groupKey].supplies.push(supply);

        return acc;
      }, [] as any);

      result.push({
        key: group.deliveryDate as string,
        type: ESupplyGroupType.Date,
        label: getSupplyFormattedDate('DD.MM.YYYY, dddd', group.deliveryDate as string),
      });
      Object.values(groupedSupplies).forEach((item) => {
        result.push({
          key: item.key,
          type: ESupplyGroupType.Group,
          data: item,
        });
      });
    });

  return result;
};

export const renderSupplyListRecords = (account: IAccountShort | undefined = undefined, navigate: (value: string) => void) => {
  return [
    {
      key: 'name',
      render: (_, record) => {
        if (record.type === ESupplyGroupType.Date) {
          return null;
        }

        const supply = record.data as ISupply;

        return (
          <div className="supply-list__table-item-name">
            <Image size={60} src={supply.good.images?.[0]} />

            <div style={{ flex: 1 }}>
              <div className="text-controls-accent mb-1 ellipsis" title={supply.good.name}>
                {supply.good.name}
              </div>

              <div className="supply-list__table-item-name-container">
                {supply.good.sellerCode && (
                  <div className="text-tag color-dark-grey" title={supply.good.sellerCode}>
                    {supply.good.sellerCode}
                  </div>
                )}

                {supply.good.buyerCode && (
                  <div className="text-tag-accent color-bright-green" title={supply.good.buyerCode}>
                    {supply.good.buyerCode}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      key: 'id',
      render: (_, record) => {
        if (record.type === ESupplyGroupType.Date) {
          return null;
        }

        const supply = record.data as ISupply;
        const requestId = getRequestIdForView(supply.request.id, supply.request.createdAt);

        return (
          <div>
            <div className="text-controls-accent mb-1 ellipsis" title={requestId}>
              {requestId}
            </div>

            <span className="text-tag color-dark-grey ellipsis">Номер заказа</span>
          </div>
        );
      },
      width: 240,
    },
    {
      key: 'count',
      render: (_, record) => {
        if (record.type === ESupplyGroupType.Date) {
          return null;
        }

        const supply = record.data as ISupply;

        return (
          <div>
            <div className="text-controls-accent mb-1 ellipsis" title={countFormatter(supply.count)}>
              {countFormatter(supply.count)}
            </div>

            <span className="text-tag color-dark-grey ellipsis" title={`${priceFormatter(supply.price)}/ед.`}>
              {`${priceFormatter(supply.price)}/ед.`}
            </span>
          </div>
        );
      },
      width: 240,
    },
    {
      key: 'amount',
      render: (_, record) => {
        if (record.type === ESupplyGroupType.Date) {
          return null;
        }

        const supply = record.data as ISupply;
        const isSelfRegistered = getIsSelfRegistered(account);
        const priceToDisplay = getPriceToDisplay(supply.price, supply.priceWithTaxes, account);

        return (
          <div>
            <div className="text-controls-accent mb-1 ellipsis" title={priceFormatter(supply.count * priceToDisplay)}>
              {priceFormatter(supply.count * priceToDisplay)}
            </div>

            <span className="ellipsis" title={priceFormatter(supply.count * supply.priceWithTaxes)}>
              <span className="ff-regular fs-xs color-dark-grey">Сумма с НДС</span>
              {!isSelfRegistered && (
                <span className="ff-regular fs-xs color-dark-grey">{` ${priceFormatter(supply.count * supply.priceWithTaxes)}`}</span>
              )}
            </span>
          </div>
        );
      },
      width: 240,
    },
    {
      key: 'status',
      title: 'Статус заказа',
      render: (_, record) => {
        if (record.type === ESupplyGroupType.Date) {
          return null;
        }

        const supply = record.data as ISupply;

        return (
          <div className="supply-list__table-item-status-container">
            <SupplyStatus status={supply.status} />
          </div>
        );
      },
      width: 200,
    },
    {
      key: 'actions',
      render: (_, record) => {
        if (record.type === ESupplyGroupType.Date) {
          return null;
        }

        const supply = record.data as ISupply;

        return (
          <Button
            className="button-circle secondary"
            icon={<ArrowRightLong />}
            onClick={() => navigate(`/request/${supply.requestId}?supplyId=${supply.id}`)}
          />
        );
      },
      onCell: () => {
        return {
          style: { paddingLeft: 0 },
        };
      },
      width: 52,
    },
  ] as ColumnsType<ISupplyGroupItem>;
};

export const renderGroupedSupplyListRecords = (role?: string, account?: IAccountShort) => {
  const asSellerManager = role === EUserRole.SellerManager;

  const records = [
    {
      key: 'subdivision',
      render: (_, record) => {
        if (record.type === ESupplyGroupType.Date) {
          return null;
        }

        const data = record.data as IGroupedSupplyGroupData;

        return (
          <div>
            <div>
              <Label icon={<Affiliate className="icon-affiliate-dark-grey" />} name={data.subdivision.name} ellipsis />
            </div>

            <span className="text-tag color-dark-grey" style={{ marginLeft: 6 }}>
              Закупщик
            </span>
          </div>
        );
      },
      width: 266,
    },
    {
      key: 'user',
      render: (_, record) => {
        if (record.type === ESupplyGroupType.Date) {
          return null;
        }

        const data = record.data as IGroupedSupplyGroupData;

        return (
          <div>
            <div>
              <Label
                icon={<User className="icon-user-dark-grey" />}
                name={getUserName.fullName(data.user.lastName, data.user.firstName, data.user.patronymic)}
                ellipsis
              />
            </div>

            <span className="text-tag color-dark-grey" style={{ marginLeft: 6 }}>
              Заявитель
            </span>
          </div>
        );
      },
      width: 278,
    },
    {
      key: 'seller',
      render: (_, record) => {
        if (record.type === ESupplyGroupType.Date) {
          return null;
        }

        const data = record.data as IGroupedSupplyGroupData;

        return (
          <div>
            <div>
              <Label icon={<Delivery className="icon-delivery-dark-grey" />} name={data.seller.name} ellipsis />
            </div>

            <span className="text-tag color-dark-grey" style={{ marginLeft: 6 }}>
              Поставщик
            </span>
          </div>
        );
      },
      width: 278,
      hidden: asSellerManager,
    },
    {
      key: 'address',
      render: (_, record) => {
        if (record.type === ESupplyGroupType.Date) {
          return null;
        }

        const data = record.data as IGroupedSupplyGroupData;

        return (
          <div>
            <div>
              <Label icon={<Delivery className="icon-delivery-dark-grey" />} name={data.address.name} ellipsis />
            </div>

            <span className="text-tag color-dark-grey" style={{ marginLeft: 6 }}>
              Адрес доставки
            </span>
          </div>
        );
      },
      width: 278,
    },
    {
      key: 'amount',
      render: (_, record) => {
        if (record.type === ESupplyGroupType.Date) {
          return null;
        }

        const isSelfRegistered = getIsSelfRegistered(account);
        const data = record.data as IGroupedSupplyGroupData;
        const amount = data.supplies.reduce((acc, supply) => {
          const priceToDisplay = getPriceToDisplay(supply.price, supply.priceWithTaxes, account);
          return acc + supply.count * priceToDisplay;
        }, 0);
        const amountWithTaxes = data.supplies.reduce((acc, supply) => acc + supply.count * supply.priceWithTaxes, 0);

        return (
          <div>
            <div>
              <div className="text-controls-accent ellipsis" title={priceFormatter(amount)}>
                {priceFormatter(amount)}
              </div>
            </div>

            <span className="ellipsis" title={priceFormatter(amountWithTaxes)}>
              <span className="ff-regular fs-xs color-dark-grey">Сумма с НДС</span>
              {!isSelfRegistered && (
                <span className="ff-regular fs-xs color-dark-grey">{` ${priceFormatter(amountWithTaxes)}`}</span>
              )}
            </span>
          </div>
        );
      },
      width: 240,
    },
    {
      key: 'goods',
      render: (_, record) => {
        if (record.type === ESupplyGroupType.Date) {
          return null;
        }

        const data = record.data as IGroupedSupplyGroupData;
        const images = data.supplies.map((supply) => supply.good.images?.[0]);
        const visibleImages = images.length > VISIBLE_IMAGES_LIMIT_4 ? images.slice(0, VISIBLE_IMAGES_LIMIT_3) : images;
        const hiddenImagesCount = images.length - visibleImages.length;

        return (
          <div className="supply-list__table-item-images">
            {visibleImages.map((src, index) => (
              <Image key={index} size={44} src={src} />
            ))}

            {!!hiddenImagesCount && (
              <div className="text-tag-accent supply-list__table-item-images-count">{`+${hiddenImagesCount}`}</div>
            )}
          </div>
        );
      },
      onCell: () => {
        return {
          style: { paddingRight: 0 },
        };
      },
      width: 244,
    },
  ] as ColumnsType<IGroupedSupplyGroup>;

  // @ts-ignore
  return records.filter((record) => !record.hidden);
};

export const renderGroupedSupplyListExpandedRowRecords = (
  account: IAccountShort | undefined = undefined,
  navigate: (value: string) => void,
) => {
  return [
    {
      key: 'name',
      render: (_, record) => {
        return (
          <div className="supply-list__table-item-name">
            <Image size={60} src={record.good.images?.[0]} />

            <div style={{ flex: 1 }}>
              <div className="text-controls-accent mb-1 ellipsis" title={record.good.name}>
                {record.good.name}
              </div>

              <div className="supply-list__table-item-name-container">
                {record.good.sellerCode && (
                  <div className="text-tag color-dark-grey" title={record.good.sellerCode}>
                    {record.good.sellerCode}
                  </div>
                )}

                {record.good.buyerCode && (
                  <div className="text-tag-accent color-bright-green" title={record.good.buyerCode}>
                    {record.good.buyerCode}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      key: 'id',
      render: (_, record) => {
        const requestId = getRequestIdForView(record.request.id, record.request.createdAt);

        return (
          <div>
            <div className="text-controls-accent mb-1 ellipsis" title={requestId}>
              {requestId}
            </div>

            <span className="text-tag color-dark-grey ellipsis">Номер заказа</span>
          </div>
        );
      },
      width: 240,
    },
    {
      key: 'count',
      render: (_, record) => {
        return (
          <div>
            <div className="text-controls-accent mb-1 ellipsis" title={countFormatter(record.count)}>
              {countFormatter(record.count)}
            </div>

            <span className="text-tag color-dark-grey ellipsis" title={`${priceFormatter(record.price)}/ед.`}>
              {`${priceFormatter(record.price)}/ед.`}
            </span>
          </div>
        );
      },
      width: 240,
    },
    {
      key: 'amount',
      render: (_, record) => {
        const isSelfRegistered = getIsSelfRegistered(account);
        const priceToDisplay = getPriceToDisplay(record.price, record.priceWithTaxes, account);

        return (
          <div>
            <div className="text-controls-accent mb-1 ellipsis" title={priceFormatter(record.count * priceToDisplay)}>
              {priceFormatter(record.count * priceToDisplay)}
            </div>

            <span className="ellipsis" title={priceFormatter(record.count * record.priceWithTaxes)}>
              <span className="ff-regular fs-xs color-dark-grey">Сумма с НДС</span>
              {!isSelfRegistered && (
                <span className="ff-regular fs-xs color-dark-grey">{` ${priceFormatter(record.count * record.priceWithTaxes)}`}</span>
              )}
            </span>
          </div>
        );
      },
      width: 240,
    },
    {
      key: 'status',
      title: 'Статус заказа',
      render: (_, record) => {
        return (
          <div className="supply-list__table-item-status-container">
            <SupplyStatus status={record.status} />
          </div>
        );
      },
      width: 200,
    },
    {
      key: 'actions',
      render: (_, record) => {
        return (
          <Button
            className="button-circle secondary"
            icon={<ArrowRightLong />}
            onClick={() => navigate(`/request/${record.requestId}?supplyId=${record.id}`)}
          />
        );
      },
      onCell: () => {
        return {
          style: { paddingLeft: 0 },
        };
      },
      width: 52,
    },
  ] as ColumnsType<ISupply>;
};
